import { Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Radio } from 'lucide-react';

import { describeModel, SPORT_LABELS, type Sport, type SportLabels } from './sport';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  describeClock,
  type LegPlan,
  type MarketOutlook,
  type PreviewResponse,
  type Side,
  type SidelineApi,
  type TapPlan
} from '@/api/sideline';

/** Fast enough to watch a book move, slow enough not to hammer the service during a match. */
const POLL_MS = 1000;

const SIDES: readonly Side[] = ['A', 'B'];

function cents(value: number | null | undefined, digits = 1): string {
  return typeof value === 'number' ? `${(value * 100).toFixed(digits)}¢` : '—';
}

function money(value: number | null | undefined): string {
  return typeof value === 'number' ? `$${value.toFixed(2)}` : '—';
}

/**
 * Why a tap would be refused, in words. The service returns its own skip reasons; spelling them out
 * here is the difference between a monitor and a log.
 */
const BLOCKED_REASONS: Record<string, string> = {
  stale_book: 'no live book — the stream is quiet or disconnected',
  empty_book: 'nothing quoted on this leg',
  spread_too_wide: 'spread is wider than the pay-up budget, so there is nothing to reach with',
  price_too_low: 'below 5¢ — too thin and the fee curve is worst value there',
  price_too_high: 'above 90¢ — more than the VAR budget allows for a goal that may not stand',
  no_edge: 'the goal is already priced in; the limit lands under the ask',
  no_rung: 'no pre-signed order at that price',
  no_fill: 'Kalshi killed the immediate-or-cancel order with nothing matched',
  no_market: 'this market has no twin on the other venue'
};

function LegCard({ plan, labels }: { plan: LegPlan; labels: SportLabels }) {
  const firing = !plan.blockedBy;
  return (
    <Card className={firing ? 'border-emerald-600' : 'border-amber-700'}>
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-baseline gap-2 text-base">
          {plan.venue === 'kalshi' ? <Badge variant="outline">Kalshi</Badge> : null}
          <span>{plan.label}</span>
          <Badge variant={firing ? 'default' : 'destructive'}>{firing ? 'would fire' : 'would skip'}</Badge>
          {!plan.fresh ? <Badge variant="destructive">stale</Badge> : null}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{plan.why}</p>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <span className="text-muted-foreground">book</span>
          <span className="text-right font-mono">
            {cents(plan.bid, 0)} / {cents(plan.ask, 0)}
            <span className="ml-2 opacity-60">{plan.askSize.toFixed(0)} on the ask</span>
          </span>

          <span className="text-muted-foreground">spread</span>
          <span className="text-right font-mono">{cents(plan.spread)}</span>

          <span className="text-muted-foreground">worth after the {labels.goal}</span>
          <span className="text-right font-mono">{cents(plan.fair)}</span>

          <span className="text-muted-foreground font-semibold">limit it would post</span>
          <span className="text-right font-mono font-semibold">{cents(plan.limit, 0)}</span>

          <span className="text-muted-foreground">reach above the ask</span>
          <span className="text-right font-mono">{plan.reach === null ? '—' : `+${cents(plan.reach)}`}</span>

          <span className="text-muted-foreground">size · most it can cost</span>
          <span className="text-right font-mono">
            {plan.shares} sh · {money(plan.maxCost)}
          </span>
        </div>

        {plan.blockedBy ? (
          <p className="rounded border border-amber-700 px-2 py-1 text-xs text-amber-500">
            <strong>{plan.blockedBy}</strong> — {BLOCKED_REASONS[plan.blockedBy] ?? 'refused by a gate'}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Everything one tap would do: the legs it buys, each through the service's own gate chain. */
function TapSection({ tap, accent, labels }: { tap: TapPlan; accent: string; labels: SportLabels }) {
  const firing = tap.legs.filter(leg => !leg.blockedBy).length;
  const maxCost = tap.legs.reduce((total, leg) => total + (leg.blockedBy ? 0 : (leg.maxCost ?? 0)), 0);
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className={`rounded px-2 py-0.5 text-sm font-semibold text-white ${accent}`}>{tap.team} scored</span>
        <span className="font-mono text-sm text-muted-foreground">{tap.scoreline}</span>
        <span className="text-xs text-muted-foreground">
          {firing} of {tap.legs.length} legs would fire · up to {money(maxCost)}
        </span>
      </div>
      <div className="grid gap-3 lg:grid-cols-3">
        {tap.legs.map(leg => (
          <LegCard key={`${leg.venue}:${leg.leg}`} plan={leg} labels={labels} />
        ))}
      </div>
    </section>
  );
}

/** What a tap does to one market, in one cell: which side it buys and what that side becomes worth. */
function TapCell({ market, side }: { market: MarketOutlook; side: Side }) {
  const tap = market.taps[side];
  if (tap.buys === null) {
    return (
      <td className="py-1 pr-4 text-right font-mono text-muted-foreground/60">
        {tap.fairAfter === null ? '—' : cents(tap.fairAfter, 0)}
      </td>
    );
  }
  const worth = tap.fairAfter === null ? null : tap.buys === 'yes' ? tap.fairAfter : 1 - tap.fairAfter;
  const settles = worth !== null && worth >= 0.995;
  return (
    <td className={`py-1 pr-4 text-right font-mono ${settles ? 'text-emerald-400 font-semibold' : 'text-emerald-500'}`}>
      buy {market.legLabels[tap.buys === 'yes' ? 0 : 1]} → {cents(worth, 0)}
      {settles ? ' ✓' : ''}
    </td>
  );
}

/**
 * The whole fixture in one table: every armed market, its book, what the model makes of it now,
 * and what each tap would do to it — bought or not. The tap sections above show only the legs that
 * would fire; this is where a market that does *not* trade is visible, and why.
 */
function AllMarkets({ data }: { data: PreviewResponse }) {
  const markets = data.markets ?? [];
  const groups = Array.from(new Set(markets.map(m => m.group)));
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Every market on the fixture</CardTitle>
        <p className="text-xs text-muted-foreground">
          {markets.length} armed, on Polymarket, on Kalshi, or both — a tap fires each market on every venue that lists
          it. A row buys nothing on a tap when the goal moves it by under 10¢, or when the model cannot price it.
          Nothing is bought above 90¢ whatever the model says — that is the budget for a goal being chalked off. Prices
          are the first outcome&apos;s ({markets[0]?.outcomes[0] ?? 'Yes'} side); the other side is one minus.
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="pb-1 pr-4 font-normal">market</th>
                <th className="pb-1 pr-4 text-right font-normal">Polymarket</th>
                <th className="pb-1 pr-4 text-right font-normal">Kalshi</th>
                <th className="pb-1 pr-4 text-right font-normal">held</th>
                <th className="pb-1 pr-4 text-right font-normal">fair now</th>
                <th className="pb-1 pr-4 text-right font-normal">if {data.teams?.A} scores</th>
                <th className="pb-1 pr-4 text-right font-normal">if {data.teams?.B} scores</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(group => (
                <Fragment key={group}>
                  <tr>
                    <td
                      colSpan={7}
                      className="pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                    >
                      {group}
                    </td>
                  </tr>
                  {markets
                    .filter(m => m.group === group)
                    .map(market => {
                      const yes = market.book.yes;
                      const stale = yes && !yes.fresh;
                      const kYes = market.kalshiBook?.yes ?? null;
                      const kStale = kYes && !kYes.fresh;
                      const held = market.held;
                      const kHeld = market.kalshiHeld;
                      return (
                        <tr key={market.key} className="border-t border-border/40">
                          <td className="whitespace-nowrap py-1 pr-4">
                            {market.label}
                            {!market.polymarket ? (
                              <Badge variant="outline" className="ml-2">
                                Kalshi only
                              </Badge>
                            ) : null}
                          </td>
                          <td className={`py-1 pr-4 text-right font-mono ${stale ? 'text-amber-500' : ''}`}>
                            {market.polymarket
                              ? `${cents(yes?.bid, 0)} / ${cents(yes?.ask, 0)}${stale ? ' stale' : ''}`
                              : '—'}
                          </td>
                          <td className={`py-1 pr-4 text-right font-mono ${kStale ? 'text-amber-500' : ''}`}>
                            {market.kalshiTicker
                              ? `${cents(kYes?.bid, 0)} / ${cents(kYes?.ask, 0)}${kStale ? ' stale' : ''}`
                              : '—'}
                          </td>
                          <td className="py-1 pr-4 text-right font-mono text-muted-foreground">
                            {held.yes || held.no
                              ? `${held.yes} ${market.outcomes[0]} · ${held.no} ${market.outcomes[1]}`
                              : '—'}
                            {kHeld && (kHeld.yes || kHeld.no)
                              ? ` · K ${kHeld.yes} ${market.outcomes[0]} · ${kHeld.no} ${market.outcomes[1]}`
                              : ''}
                          </td>
                          <td className="py-1 pr-4 text-right font-mono">{cents(market.fairNow, 0)}</td>
                          <TapCell market={market} side="A" />
                          <TapCell market={market} side="B" />
                        </tr>
                      );
                    })}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {data.ignored?.length ? (
          <p className="mt-3 text-xs text-muted-foreground">
            <span className="font-semibold">Not armed:</span> {data.ignored.join(' · ')}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** The read-only dry run of both taps, for one sport: `api` is the service it polls, `sport` the words. */
export function SidelineMonitorPage({ api, sport }: { api: SidelineApi; sport: Sport }) {
  const labels = SPORT_LABELS[sport];
  const { data, isLoading, error } = useQuery<PreviewResponse>({
    queryKey: ['sideline-preview', api.service],
    queryFn: () => api.preview(),
    refetchInterval: POLL_MS,
    retry: false
  });

  if (isLoading) {
    return <Loader2 className="size-5 animate-spin" />;
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="size-5" /> Cannot reach {labels.service}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {error instanceof Error ? error.message : String(error)}
        </CardContent>
      </Card>
    );
  }

  if (!data?.armed || !data.taps || !data.teams) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Nothing armed</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Arm a {labels.match} on the <strong>{labels.name}</strong> page and this fills in.
        </CardContent>
      </Card>
    );
  }

  const usingModel = Boolean(data.model && data.minute !== null);
  // The clock comes off the Polymarket event — period, minute and score — so "not started" and
  // "2H 58'" are what the feed says, not a guess from a typed-in kick-off time.
  const clock = data.clock;
  const clockDown = !clock || clock.phase === 'no-clock' || clock.stale;
  const feed = clock?.feedScore ?? null;
  const feedDisagrees = feed !== null && (feed.A !== (data.score?.A ?? 0) || feed.B !== (data.score?.B ?? 0));
  const nearNow = (minute: number) =>
    data.minute !== null && data.minute !== undefined && Math.abs(minute - data.minute) < 8;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold">{labels.name} Monitor</h1>
        {data.simulated ? <Badge variant="outline">PAPER</Badge> : null}
        <Badge variant={data.bookConnected ? 'default' : 'destructive'} className="gap-1">
          <Radio className="size-3" />
          {data.bookConnected ? 'book live' : 'book disconnected'}
        </Badge>
        {data.kalshi ? (
          <Badge variant={data.kalshi.bookConnected ? 'default' : 'destructive'} className="gap-1">
            <Radio className="size-3" />
            {data.kalshi.bookConnected ? `Kalshi · ${data.kalshi.markets} markets` : 'Kalshi book stale'}
          </Badge>
        ) : null}
        <span className="text-xs text-muted-foreground">
          Read-only. Nothing on this page places an order — it runs the same gates the trigger runs and reports the
          answer, for every leg a tap would buy.
        </span>
      </div>

      <div className="flex flex-wrap items-baseline gap-2 text-sm text-muted-foreground">
        <span>
          {data.teams.A} vs {data.teams.B}
        </span>
        <span>·</span>
        <span className={clockDown ? 'text-amber-500' : ''}>{describeClock(clock, labels.clock)}</span>
        <span>·</span>
        <span>
          score {data.score?.A ?? 0}–{data.score?.B ?? 0}{' '}
          <span className={feedDisagrees ? 'text-amber-500' : 'opacity-60'}>
            {feed ? `(feed ${feed.A}–${feed.B})` : '(from taps)'}
          </span>
          {data.firstScorer ? <span className="opacity-60"> · {data.teams[data.firstScorer]} scored first</span> : null}
        </span>
        <span>·</span>
        {usingModel ? (
          <span>
            {describeModel(data.model)}
            {data.modelFittedAt ? (
              <span className={data.modelSource === 'listing' ? ' text-amber-500' : ' text-muted-foreground'}>
                {' '}
                · fitted {new Date(data.modelFittedAt).toLocaleTimeString([], { hour12: false })} from the{' '}
                {data.modelSource ?? 'book'}
              </span>
            ) : null}
          </span>
        ) : (
          <span className="text-amber-500">
            no model — flat pay-up of {Math.round((data.payUpBudget ?? 0) * 100)}¢ on the moneyline only
            {!data.model ? ' (not fitted at arm time)' : clockDown ? ' (clock unavailable)' : ''}
          </span>
        )}
      </div>

      <TapSection tap={data.taps.A} accent="bg-blue-700" labels={labels} />
      <TapSection tap={data.taps.B} accent="bg-red-700" labels={labels} />

      <AllMarkets data={data} />

      {usingModel && data.projection ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              What a {labels.goal} is worth, through the {labels.match}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-3 text-xs text-muted-foreground">
              Fair value of each leg a tap buys, if that goal went in at each minute, at the current score. The flat
              rule this replaced was one number for all of these.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="pb-1 pr-4 font-normal">minute</th>
                    {data.projection.minutes.map(minute => (
                      <th key={minute} className="pb-1 pr-4 text-right font-normal">
                        {minute}&apos;
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {SIDES.map(side => (
                    <Fragment key={side}>
                      <tr>
                        <td
                          colSpan={data.projection!.minutes.length + 1}
                          className="pt-3 pb-1 font-sans text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                        >
                          {data.teams?.[side]} scored
                        </td>
                      </tr>
                      {data.projection?.[side].map(leg => (
                        <tr key={leg.leg}>
                          <td className="whitespace-nowrap py-1 pr-4 font-sans text-muted-foreground">{leg.label}</td>
                          {leg.points.map(p => (
                            <td
                              key={p.minute}
                              className={`py-1 pr-4 text-right ${nearNow(p.minute) ? 'text-emerald-500' : ''}`}
                            >
                              {cents(p.fair, 0)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
